import { useTranslation } from '@cobliteam/i18n';
import { Button } from '@cobliteam/ui/components';
import { format } from '@cobliteam/utils/date';
import { useCallback, useEffect, useRef } from 'react';
import { GeoJSON, MapContainer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Location } from '@cobliteam/common-types';
import type { GeofenceType } from '@cobliteam/common-types/src/geofenceV3';
import L from 'leaflet';
import { parseAsString, useQueryStates } from 'nuqs';
// Fix for default markers in react-leaflet
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import { Link, useLocation } from 'react-router-dom';
import useSearch from '../../hooks/useSearch';
import { mapStyles } from '../../utils/mapStyle';

export interface CardMapGeojsonProps {
  id?: string;
  title?: string;
  latitude?: number | null;
  longitude?: number | null;
  updatedAt?: number | null;
  linkTo: string;
  type: GeofenceType;
  geojson?: GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry;
  zoom?: number;
  height?: string;
  icon: any;
  color: string;
}

export interface GeojsonStyle {
  color: string;
  weight: number;
  fillColor: string;
  fillOpacity: number;
}

export interface MapConfig {
  center: [number, number];
  zoom: number;
  height: string;
  geojsonStyle: GeojsonStyle;
}

const CardMapGeojson = ({ id, latitude, icon, color, longitude, updatedAt, type, linkTo, title, geojson, zoom = 7 }: CardMapGeojsonProps) => {
  const { t } = useTranslation('global-search');
  const { setIsOpen } = useSearch();
  const mapRef = useRef<L.Map>(null);
  const location = useLocation();

  const [geofenceTab, setGeofenceTab] = useQueryStates({
    geofenceId: parseAsString,
    tab: parseAsString,
  });

  const isLocationValid = latitude && longitude;
  if (!isLocationValid) return null;

  // Center coordinates for the map
  const center: [number, number] = [latitude, longitude];

  // GeoJSON styling
  const geojsonStyle = {
    color: color,
    weight: 2,
    fillColor: color,
    fillOpacity: 0.2,
  };

  useEffect(() => {
    // Get bounds from geojson and center the map
    if (mapRef.current && geojson) {
      const map = mapRef.current;
      const geojsonLayer = L.geoJSON(geojson);
      const bounds = geojsonLayer.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds);
      }
    }
  }, [geojson, mapRef.current]);

  const openGeofence = useCallback(() => {
    setIsOpen(false);

    // This workaround handles the case of global search from monitoring page. Nuqs and react-router do not sync well together
    // when used on the same context, so we need to manually set the geofenceId query param in this case. This shouldn't be
    // needed when using React Router v6 with Nuqs.
    if (geofenceTab.geofenceId && location.pathname === Location.MONITORING) {
      setGeofenceTab({ geofenceId: id, tab: 'geofences' });
    }
  }, [id, location.pathname, setGeofenceTab, geofenceTab.geofenceId, setIsOpen]);

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex flex-row items-center justify-between'>
        <div className='flex flex-col'>
          <div className='flex flex-row items-center gap-1'>
            <div className='font-medium text-xs'>{title || t('card_map.vehicle_location_title')}</div>
          </div>
          <div className='flex flex-row gap-1 text-gray-9'>
            <div className='text-xs'>{t('card_map.last_update')}</div>
            <div className='text-xs'>{updatedAt ? format(updatedAt, 'dd/MM/yyyy HH:mm') : '---'}</div>
          </div>
        </div>
        <div>
          <Link to={linkTo} onClick={openGeofence}>
            <Button className='h-6 text-xs' variant='link' size='sm'>
              {t('card_map.view_on_map')}
            </Button>
          </Link>
        </div>
      </div>

      <div className='h-[200px] w-full overflow-hidden rounded-md border border-gray-3' key={id}>
        <Link to={linkTo} onClick={openGeofence}>
          <MapContainer
            className='transition-all duration-300 hover:scale-110'
            center={center}
            zoom={zoom}
            style={{ height: '100%', width: '100%' }}
            ref={mapRef}
            zoomControl={false}
            attributionControl={false}
          >
            {/* Google Maps Tile Layer */}
            <ReactLeafletGoogleLayer
              zIndex={0}
              // @ts-ignore
              type={'roadmap' as const}
              detectRetina
              styles={mapStyles}
              useGoogMapsLoader={false}
            />
            {/* GeoJSON Layer */}
            {geojson && <GeoJSON data={geojson} style={geojsonStyle} />}

            {/* Point Marker */}
            {type !== 'PATH' && <Marker autoPan={false} position={center} icon={icon} />}
          </MapContainer>
        </Link>
      </div>
    </div>
  );
};

export default CardMapGeojson;
